import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  CursorArrowRaysIcon, HandRaisedIcon, PlusCircleIcon, LinkIcon, TrashIcon,
  PlayIcon, StopIcon, ArrowPathIcon, QuestionMarkCircleIcon, ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { startSimulation, stopSimulation } from '../store/simulationSlice';

// LA BARRA DEL LLENÇ CAUSAL.
//
// Abans això eren dues coses en dos llocs: un panell de 256 px a l'esquerra amb els cinc modes i
// un text d'ajuda —que ja era, paraula per paraula, dins de la finestra d'ajuda— i una franja de
// controls de simulació **a sota de tot**, que en una pantalla d'aula queda fora de vista.
//
// Amb el panell de propietats de 320 px a l'altra banda, **576 px de 1366 no eren llenç**: el 42 %.
// Ara tot el que es fa servir mentre es dibuixa és en una sola fila a l'alçada dels ulls, i el
// llenç ocupa l'amplada sencera.

const MODES = [
  ['select', CursorArrowRaysIcon, 'help.select'],
  ['pan', HandRaisedIcon, 'help.pan'],
  ['variable', PlusCircleIcon, 'help.addVar'],
  ['link', LinkIcon, 'help.addLink'],
  ['delete', TrashIcon, 'help.del'],
];

const VELOCITATS = [0.5, 1, 2];

export default function BarraLlenc({ mode, onModeChange, canvasRef, titolModel, peuPDF }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isRunning } = useSelector((state) => state.simulations);
  const [ajustos, setAjustos] = useState({ speed: 1, showValues: true, showParticles: true });

  useEffect(() => {
    if (canvasRef.current) canvasRef.current.updateSettings(ajustos);
  }, [ajustos, canvasRef]);

  const engega = () => {
    dispatch(startSimulation());
    canvasRef.current?.startSimulation(ajustos);
  };
  const atura = () => {
    dispatch(stopSimulation());
    canvasRef.current?.stopSimulation();
  };
  const reinicia = () => {
    dispatch(stopSimulation());
    canvasRef.current?.resetSimulation();
  };

  const boto = (contingut, props = {}) => (
    <button type="button" {...props}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
                  border transition-colors ${props.actiu
        ? 'bg-primary-700 text-white border-primary-700'
        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'} ${props.className || ''}`}>
      {contingut}
    </button>
  );

  return (
    <div className="bg-white border-b border-gray-200 px-3 py-2 flex items-center gap-1.5 flex-wrap">
      {MODES.map(([clau, Icona, etiqueta]) => (
        <button key={clau} type="button" onClick={() => onModeChange(clau)}
          title={t(etiqueta)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
                      border transition-colors ${mode === clau
            ? 'bg-primary-700 text-white border-primary-700'
            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
          <Icona className="h-4 w-4" />
          <span className="hidden md:inline">{t(etiqueta)}</span>
        </button>
      ))}

      <span className="w-px h-6 bg-gray-200 mx-1.5" />

      <button type="button" onClick={isRunning ? atura : engega}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border
                    transition-colors ${isRunning
          ? 'bg-danger-600 text-white border-danger-600 hover:bg-danger-700'
          : 'bg-primary-700 text-white border-primary-700 hover:bg-primary-800'}`}>
        {isRunning ? <StopIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
        {isRunning ? t('simulation.stop') : t('simulation.run')}
      </button>

      {boto(<><ArrowPathIcon className="h-4 w-4" />{t('simulation.reset')}</>, { onClick: reinicia })}

      <div className="flex items-center gap-1 ml-1">
        <span className="text-[11px] text-gray-500">{t('simulation.speed')}</span>
        {VELOCITATS.map((v) => (
          <button key={v} type="button" onClick={() => setAjustos({ ...ajustos, speed: v })}
            className={`px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${
              ajustos.speed === v
                ? 'bg-primary-700 text-white border-primary-700'
                : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'}`}>
            {String(v).replace('.', ',')}×
          </button>
        ))}
      </div>

      {/* Els dos interruptors només fan efecte mentre la simulació corre. Abans no ho deia res i
          es podien clicar amb tot aturat sense que passés res; ara s'apaguen. */}
      <div className={`flex items-center gap-3 ml-1 ${isRunning ? '' : 'opacity-50'}`}
           title={isRunning ? undefined : t('sim2.nomesCorrent')}>
        {[['showValues', 'sim2.showValues'], ['showParticles', 'sim2.particles']].map(([k, etq]) => (
          <label key={k} className="flex items-center gap-1.5 text-[11px] text-gray-600">
            <input type="checkbox" checked={ajustos[k]} disabled={!isRunning}
              onChange={(e) => setAjustos({ ...ajustos, [k]: e.target.checked })}
              className="h-3.5 w-3.5 rounded-md border-gray-300 text-primary-700 focus:ring-primary-600" />
            {t(etq)}
          </label>
        ))}
      </div>

      {/* Exportar. El PDF surt VECTORIAL perquè el llenç ja és SVG i passa per la impressió del
          navegador: es pot ampliar sense que es piqui. El diàleg que s'obre és el d'imprimir, i
          allà es tria «Desa com a PDF». */}
      {boto(<><ArrowDownTrayIcon className="h-4 w-4" /><span className="hidden lg:inline">PDF</span></>,
            { onClick: () => canvasRef.current?.exportaPDF(titolModel, peuPDF), title: t('tools.exportaPDF') })}

      {/* L'ajuda, sense caixa de botó: és una icona que sempre és allà, no una acció que es
          faci servir. I la importació de fitxers se n'ha anat: ningú no importa models a mà en
          una classe, i cada botó que no es fa servir mai és soroll damunt del llenç. */}
      <button type="button" onClick={() => canvasRef.current?.obreAjuda()}
        title={t('help.title')} aria-label={t('help.title')}
        className="ml-auto text-gray-500 hover:text-gray-700 p-1 rounded-md hover:bg-gray-100">
        <QuestionMarkCircleIcon className="h-5 w-5" />
      </button>
    </div>
  );
}
